import isFunction from 'lodash/isFunction';
import React, { Fragment, memo, useCallback, useMemo } from 'react';
import classNames from 'classnames';

import { UiCleanRadioGroupProps, UiRadioGroupPropsValue } from 'src/components/UI/radioGroup/types';
import { UiTextTooltip } from 'src/components/UI/TextTooltip';
import { UiMereLabel } from 'src/components/UI/UiMereLabel';
import { useCleanHoverErrors } from 'src/components/UI/inputs/useHoverErrors';
import { UiHoverTooltip } from 'src/components/UI/HoverTooltip';
import { SafeAnyType } from 'src/utils/safeAny';
import { getTestId } from 'src/utils/testing';
import { useInputCleanBaseHook } from 'src/utils/hooks/inputs';
import { getUniqueId } from 'src/utils/getUniqueId';
import { MaterialErrorMessage } from 'src/components/UI/common/MaterialErrorMessage';

export const UiCleanRadioGroup = memo<UiCleanRadioGroupProps>(
  ({
    formProps: { clearErrors, onChange: formOnChange, register, unregister, getSubscribeProps },
    options,
    name,
    disabled,
    onChange,
    required,
    showHoverError,
    errorLabel,
    dataTestId,
    material,
    customElement = true,
    labelClassName,
    className,
  }) => {
    const preparedOptions = useMemo(() => options.map((option) => ({ ...option, id: getUniqueId() })), [options]);

    const inputOptions = useMemo(() => ({ required, errorLabel }), [errorLabel, required]);

    const [fieldValue, errors] = useInputCleanBaseHook({
      getSubscribeProps,
      name,
      inputOptions,
      unregister,
      register,
      type: 'radio',
    });

    const { errorString, onChangeInputHover } = useCleanHoverErrors({
      name,
      clearErrors,
      errors,
      showAnyError: showHoverError,
    });

    const onChangeLocal = useCallback(
      (value: UiRadioGroupPropsValue, optionDisabled?: boolean) => () => {
        if (!disabled && !optionDisabled) {
          formOnChange(name, value);

          onChangeInputHover();

          if (isFunction(onChange)) {
            onChange(value);
          }
        }
      },
      [disabled, formOnChange, name, onChange, onChangeInputHover]
    );

    // eslint-disable-next-line react/no-unstable-nested-components
    const TooltipWrapper = ({
      children,
      tooltip,
    }: {
      children: SafeAnyType;
      tooltip: UiCleanRadioGroupProps['options'][0]['tooltip'];
    }) =>
      tooltip ? (
        <UiTextTooltip showHover text={tooltip}>
          {children}
        </UiTextTooltip>
      ) : (
        children
      );

    return (
      <div className={`radio-button-group${material ? '-material' : ''}${className ? ` ${className}` : ''}`}>
        {preparedOptions.map(({ id, label, value, tooltip, disabled: itemDisabled, customTooltip, innerComponent }) => {
          const hasCustomTooltip = !!customTooltip;

          const component = (
            <TooltipWrapper tooltip={material ? tooltip : null}>
              <p
                className={classNames(`radio-button${material ? '-material' : ''}`, {
                  error: !!errorString && !material,
                  custom: customElement,
                })}
              >
                <input
                  id={id}
                  className={classNames('with-gap', {
                    error: !!errorString && material,
                  })}
                  type="radio"
                  data-name={name}
                  checked={value === fieldValue}
                  onChange={onChangeLocal(value, itemDisabled)}
                  disabled={disabled || itemDisabled}
                  data-testid={dataTestId ? getTestId('radiogroup', dataTestId, name)(value) : undefined}
                />

                <UiMereLabel htmlFor={id} className={labelClassName}>
                  <TooltipWrapper tooltip={material ? null : tooltip}>{label}</TooltipWrapper>
                </UiMereLabel>
                {material && <MaterialErrorMessage error={errorString} />}
              </p>
            </TooltipWrapper>
          );

          return (
            // errors tooltip
            <Fragment key={id}>
              <UiHoverTooltip
                text={errorString}
                id={name}
                render={!material && !!errorString}
                tooltipClassName="error-tooltip"
                noWrapper
              >
                {hasCustomTooltip ? (
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  <UiHoverTooltip {...customTooltip} render={hasCustomTooltip}>
                    {component}
                  </UiHoverTooltip>
                ) : (
                  component
                )}
              </UiHoverTooltip>
              {value === fieldValue && innerComponent}
            </Fragment>
          );
        })}
      </div>
    );
  }
);
